import { formatMmAsCm } from '~/utils/units';
import { Measurement } from '~/domain/measurement/measurement';
import { SampleAggregate } from '~/domain/statistics/sampleAggregate';

export const NOT_AVAILABLE = '—';

/**
 * Display formatting for measurements. This is the application/UI boundary
 * where the domain unit (mm) is converted to the display unit (cm), per
 * docs/ui_specs.md. Distances are macro distances → 2 decimal places in
 * cm; angles are reported in degrees → 1 decimal place. The unit label is
 * always included.
 */

/** Decimal places for distance display (macro distance, cm). */
const DISTANCE_DECIMALS = 2;
/** Decimal places for angle display (degrees). */
const ANGLE_DECIMALS = 1;

export const asCm = (mm: number): string => formatMmAsCm(mm, DISTANCE_DECIMALS);

/** Format a mm distance as a cm display string, e.g. `"2.54 cm"`. */
export function formatDistanceCm(distanceMm: number): string {
    return `${asCm(distanceMm)} cm`;
}

/** Format an angle in degrees as a display string, e.g. `"42.5°"`. */
export function formatAngleDeg(angleDeg: number): string {
    return `${angleDeg.toFixed(ANGLE_DECIMALS)}°`;
}

/** Format a committed measurement as its primary display value. */
export function formatMeasurementValue(measurement: Measurement): string {
    return measurement.kind === 'distance'
        ? formatDistanceCm(measurement.distanceMm)
        : formatAngleDeg(measurement.angleDeg);
}

export function formatMetricCm(valueMm: number): string {
    return Number.isFinite(valueMm) ? asCm(valueMm) : NOT_AVAILABLE;
}

const MIN_READINGS_FOR_SD = 3;

export function formatMeanWithSpreadCm(
    aggregate: SampleAggregate | null
): string {
    if (!aggregate) {
        return NOT_AVAILABLE;
    }
    if (aggregate.count < MIN_READINGS_FOR_SD) {
        return formatMetricCm(aggregate.mean);
    }
    return `${formatMetricCm(aggregate.mean)}±${formatMetricCm(aggregate.sd)}`;
}
